import { useState, useMemo, useCallback } from 'react';
import type { Span, TraceFilters, SpanType, SpanStatus } from './types';

// --- useTraceState: shared filter/expand/select state ---
//
// Lives apart from AgentTraceTable so that file exports only components: a module that mixes
// component and non-component exports defeats Fast Refresh, which then full-reloads instead of
// preserving state on every edit.

/** A new Set with `value` added if absent, removed if present. Returning a fresh Set rather than
 * mutating is what makes it safe to hand straight to a state setter. */
export function toggled<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (!next.delete(value)) next.add(value);
  return next;
}

const NO_DEFAULTS: ReadonlySet<string> = new Set();

/** Every span id that has children — the rows a caret can open or close. */
function parentIds(spans: Span[], out: string[] = []): string[] {
  for (const span of spans) {
    if (span.children?.length) {
      out.push(span.id);
      parentIds(span.children, out);
    }
  }
  return out;
}

/**
 * @param defaultExpanded Rows that start open until the viewer says otherwise — re-read on every
 *   render, so a row that becomes the live one opens as it does, while any row the viewer has
 *   toggled keeps the state they gave it.
 */
export function useTraceState(
  spans: Span[],
  defaultExpanded: ReadonlySet<string> = NO_DEFAULTS,
) {
  // The viewer's own open/closed choices, which win over `defaultExpanded`.
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(
    () => new Map(),
  );
  const expanded = new Set<string>();
  for (const id of defaultExpanded) {
    if (overrides.get(id) !== false) expanded.add(id);
  }
  for (const [id, isOpen] of overrides) if (isOpen) expanded.add(id);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = useMemo((): Span | null => {
    if (!selectedId) return null;
    const stack = [...spans];
    while (stack.length) {
      const node = stack.pop();
      if (!node) break;
      // A row merged away on display (a streamed-reasoning prefix) resolves to its survivor.
      if (node.id === selectedId || node.mergedIds?.has(selectedId)) {
        return node;
      }
      if (node.children?.length) stack.push(...node.children);
    }
    return null;
  }, [spans, selectedId]);
  const [query, setQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<TraceFilters>({
    types: new Set(),
    statuses: new Set(),
    minTokens: 0,
  });

  const expandAll = () =>
    setOverrides(new Map(parentIds(spans).map((id) => [id, true])));

  // Explicitly closed, not just cleared: clearing would let every default-open row spring back.
  const collapseAll = () =>
    setOverrides(new Map(parentIds(spans).map((id) => [id, false])));

  const toggleExpand = (id: string) =>
    setOverrides((prev) =>
      new Map(prev).set(id, !(prev.get(id) ?? defaultExpanded.has(id))),
    );

  const toggleType = useCallback(
    (type: SpanType) =>
      setFilters((prev) => ({ ...prev, types: toggled(prev.types, type) })),
    [],
  );

  const toggleStatus = useCallback(
    (status: SpanStatus) =>
      setFilters((prev) => ({
        ...prev,
        statuses: toggled(prev.statuses, status),
      })),
    [],
  );

  const setMinTokens = useCallback(
    (n: number) => setFilters((prev) => ({ ...prev, minTokens: n })),
    [],
  );

  const clearFilters = useCallback(
    () => setFilters({ types: new Set(), statuses: new Set(), minTokens: 0 }),
    [],
  );

  const toggleShowFilters = useCallback(() => setShowFilters((v) => !v), []);

  return {
    expanded,
    toggleExpand,
    expandAll,
    collapseAll,
    selected,
    setSelectedId,
    query,
    setQuery,
    showFilters,
    toggleShowFilters,
    filters,
    toggleType,
    toggleStatus,
    setMinTokens,
    clearFilters,
  };
}
